<script setup>
import { onMounted } from 'vue'
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

// 登録
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  ChartDataLabels
)

// if (Chart.registry.plugins.get('customImagePlugin')) {
//   Chart.registry.plugins.remove('customImagePlugin')
//   console.log('remove customImagePlugin')
// }
// Chart.register(customImagePlugin)

const props = defineProps({
  graphData: {
    type: Object,
    default: () => {
      return {
        past: [],
        future: [],
        labels: [],
        images: [],
        cityName: '',
        current: {
          time: '--:--',
          temp: '--.--'
        }
      }
    }
  }
})

onMounted(() => {
  const graphContainer = document.querySelector('.graph')
  if (!graphContainer) {
    console.error('Error: .graph element not found')
    return
  }

  const ctxGraph = document.createElement('canvas')

  // const displayWidth = window.innerWidth
  ctxGraph.width = 600
  ctxGraph.height = 300
  graphContainer.appendChild(ctxGraph)
  ctxGraph.style.width = '100% !important'
  ctxGraph.style.height = '100% !important'
  ctxGraph.style.display = 'block'

  const graphData = {
    labels: props.graphData.labels,
    datasets: [
      {
        label: '',
        data: props.graphData.future,
        borderColor: '#8B99B1',
        backgroundColor: 'rgba(139, 153, 177, 0.2)',
        borderWidth: 2,
        tension: 0,
        pointRadius: (ctx) => {
          const dataset = graphData.datasets[0]
          if (!dataset || !dataset.data) {
            return 0 // デフォルト値を返す
          }
          const index = ctx.dataIndex
          const value = dataset.data[index]
          if (value === undefined) {
            return 0 // デフォルト値を返す
          }
          // 配列をまわしてmaxValue, minValueをチェック
          const maxValue = Math.max(...dataset.data)
          const minValue = Math.min(...dataset.data)
          return value === maxValue || value === minValue ? 3 : 0 // 最高値と最低値のみ丸を表示
        },
        pointBackgroundColor: (ctx) => {
          const index = ctx.dataIndex
          const value = ctx.dataset.data[index]
          const maxValue = Math.max(...ctx.dataset.data)
          const minValue = Math.min(...ctx.dataset.data)
          if (value === maxValue) return '#F64D00' // 最高値の色
          if (value === minValue) return '#0075F3' // 最低値の色
          return 'transparent'
        },
        pointBorderWidth: 0
      },
      {
        label: '',
        data: props.graphData.past,
        borderColor: '#A2ACBD',
        backgroundColor: 'rgba(162, 172, 189, 0.2)',
        borderWidth: 2,
        borderDash: [5, 5],
        tension: 0,
        pointRadius: 0
      }
    ]
  }

  const FILTERED_DATA_0 = graphData.datasets[0].data.filter((value) => value !== null)
  const FILTERED_DATA_1 = graphData.datasets[1].data.filter((value) => value !== null)

  const maxValue = Math.max(...FILTERED_DATA_0, ...FILTERED_DATA_1)
  const minValue = Math.min(...FILTERED_DATA_0, ...FILTERED_DATA_1)

  const maxTodayValue = Math.max(...FILTERED_DATA_0)
  const minTodayValue = Math.min(...FILTERED_DATA_0)
  const maxTodayIndexFirst = graphData.datasets[0].data.indexOf(maxTodayValue)
  const minTodayIndexFirst = graphData.datasets[0].data.indexOf(minTodayValue)

  let maxLabel = Math.ceil(maxValue / 2) * 2 // 最高値を2刻みで切り上げ
  let minLabel = Math.floor(minValue / 2) * 2 // 最低値を2刻みで切り下げ
  if (Math.abs(maxLabel - maxValue) < 2) {
    maxLabel += 2
  }
  if (Math.abs(minLabel - minValue) < 2) {
    minLabel -= 2
  }

  const graphOptions = {
    responsive: true,
    maintainAspectRatio: false, // アスペクト比を維持しない
    layout: {
      padding: {
        left: 7,
        top: 20,
        bottom: 30 // 余白を追加して画像が見切れないようにする
      }
    },
    plugins: {
      legend: {
        display: false // ラベルを非表示
      },
      tooltip: {
        enabled: false // ツールチップを完全に無効化
      },
      datalabels: {
        display: (context) => {
          const datasetIndex = context.datasetIndex
          const dataIndex = context.dataIndex
          // 実線のデータセット（index 0）のみ、最大値と最小値にラベルを表示
          return datasetIndex === 0 && (dataIndex === maxTodayIndexFirst || dataIndex == minTodayIndexFirst)
        },
        color: (context) => {
          const value = context.dataset.data[context.dataIndex]
          return value === maxTodayValue ? '#F64D00' : '#0075F3' // 最大値は赤、最小値は青
        },
        formatter: (value) => {
          try {
            const ROUND_STRRING = String(Math.round(value))
            return ROUND_STRRING
          } catch (e) {
            console.log(e)
            return value
          }
        },
        font: {
          size: 14,
          weight: 'bold' // 見やすくするため太字に
        },
        clamp: true,
        padding: 1,
        textStrokeColor: 'white', // テキストの縁取り色
        textStrokeWidth: 2, // テキストの縁取り幅
        borderRadius: 4,
        align: (context) => {
          const dataIndex = context.dataIndex
          const dataLength = context.dataset.data.length
          const value = context.dataset.data[dataIndex]

          const isRightEdge = (dataIndex === (dataLength - 1))
          const isLeftEdge = (dataIndex === 0)

          if (isRightEdge) {
            return value === maxTodayValue ? 'top' : 'bottom'
          } else if (isLeftEdge) {
            return value === maxTodayValue ? 'top' : 'bottom'
          } else {
            return value === maxTodayValue ? 'top' : 'bottom'
          }
        }
      },
      customImagePlugin: {}
    },
    scales: {
      x: {
        ticks: {
          color: '#919191', // 色を変更
          font: () => {
            // ウィンドウ幅に応じて文字サイズを可変に
            if (window.innerWidth <= 829) {
              return { size: 10 }
            }
            return { size: 12 }
          },
          maxTicksLimit: 24, // 最大表示ラベル数を制限
          autoSkip: false, // ラベルの自動スキップを無効化
          callback: function (value, index) {
            // X軸ラベルはgraphData.labelsの内容をそのまま表示
            return graphData.labels[index] !== undefined ? graphData.labels[index] : ''
          },
          // 角度を0に固定
          minRotation: 0,
          maxRotation: 0
        },
        grid: {
          display: false // グリッド線を非表示
        }
      },
      y: {
        position: 'right', // 縦軸のラベルを右側に配置
        min: minLabel,
        max: maxLabel,
        ticks: {
          color: '#919191', // 色を変更
          font: {
            size: 12 // 文字サイズを変更
          },
          stepSize: 2,
          callback: function (value) {
            // 最低値と最高値のみ表示する
            if (value === minLabel || value === maxLabel) {
              return value
            }
            return ''
          }
        },
        grid: {
          color: '#E5E5E5',
          lineWidth: 1,
          drawBorder: false
        },
        beginAtZero: false
      }
    }
  }

  // plugins
  const imagePluginInstance = {
    id: 'customImagePlugin',
    afterDraw: (chart) => {
      function findNthIndex(array, n, predicate) {
        let count = 0
        for (let i = 0; i < array.length; i++) {
          if (predicate(array[i], i, array)) {
            if (count === n) {
              return i
            }
            count++
          }
        }
        return undefined
      }
      const ctx = chart.ctx
      const xAxis = chart.scales.x
      const images = props.graphData.images || []
      images.forEach((imageName, index) => {
        if (!imageName) return
        const offset = findNthIndex(props.graphData.labels, index, (val) => val !== '・')
        if (offset === undefined) return
        const img = new Image()
        img.src = imageName
        img.onload = () => {
          const x = xAxis.getPixelForTick(offset) - 10
          const y = xAxis.bottom + 5
          ctx.drawImage(img, x, y, 20, 20)
        }
        img.onerror = () => console.error(`Failed to load image: ${imageName}`)
      })
    }
  }

  const yAxisUnitPlugin = {
    id: 'yAxisUnit',
    beforeDraw: (chart, args, options) => {
      const { ctx } = chart
      const yAxis = chart.scales.y

      ctx.save()
      ctx.font = options.font || '10px'
      ctx.fillStyle = options.color || '#919191'

      // 単位の位置を調整
      const xPos = yAxis.left - (options.offsetX || 0)
      const yPos = yAxis.top - (options.offsetY || 10)

      ctx.fillText(options.text || '(℃)', xPos, yPos)
      ctx.restore()
    }
  }

  try {
    new Chart(ctxGraph, {
      type: 'line',
      data: graphData,
      options: graphOptions,
      plugins: [imagePluginInstance, yAxisUnitPlugin]
    })
  } catch (error) {
    console.error('Error initializing Chart:', error)
  }
})
</script>

<template>
  <article>
    <div
      id="graph__title"
      class="head"
    >
      <div class="head__block">
        <h2 class="head__title title">
          これからの気温変化
        </h2>
        <p class="head__date">
          {{ graphData?.current?.time }}現在
        </p>
      </div>
      <div class="graph__legend">
        <p class="solid">
          <span />これから
        </p>
        <p class="dashed">
          <span />これまで
        </p>
      </div>
    </div>
    <div class="tempBlock">
      <div class="head">
        <p class="tempBlock__name">
          {{ graphData?.cityName }}
        </p>
        <p class="tempBlock__value">
          {{ graphData?.current?.temp }}<span>℃</span>
        </p>
      </div>
    </div>
    <div class="graph" />
  </article>
</template>

<style scoped>
#areaMain {
  padding-top: 20px;
}

.head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom:15px;
}

.head:is(.tempBlock *) {
  margin-bottom: 0;
}

@media (min-width: 830px) {
  main .head#graph__title {
    flex-direction: row;
  }
}

.tempBlock .head {
    flex-direction: row;
}

.head__block {
  display: flex;
  align-items: center;
  gap: 10px;
}

.head__title {
  color: #101010;
  font-size: 18px;
  font-weight: 700;
  line-height: 1em;
}

@media (min-width: 830px) {
  main .head__title {
    font-size: 22px;
  }
}

.head__date {
  color: #919191;
  font-size: 13px;
  font-weight: 300;
  line-height: 18px;
}

.tempBlock {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  background-color: #F5F5F5;
}

.tempBlock__name {
  color: #303030;
  font-size: 15px;
  font-weight: 300;
  line-height: 18px;
}

.tempBlock__value {
  display: flex;
  align-items: baseline;
  gap: 3px;
  color: #303030;
  font-size: 18px;
  font-weight: 600;
  line-height: 1em;
}

.tempBlock__value span {
  color: #303030;
  font-size: 13px;
  font-weight: 600;
  line-height: 1em;
}

.graph {
  position: relative;
  width: 100%;
  aspect-ratio: 2 / 1;
  margin: 0 auto;
  padding: 20px 10px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

@media (min-width: 830px) {
  main .graph {
    aspect-ratio: 2 / 1;
  }
}

:global(.graph canvas) {
  display: block;
  width: 100% !important;
  height: 100% !important;
}
.graph__legend {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 2px 3px;
  border-radius: 3px;
}

.graph__legend p {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #303030;
  font-size: 13px;
  font-weight: 300;
  line-height: 18px;
}

span {
  width: 13px;
  height: 2px;
}

span:is(.solid *) {
  border-top: 2px solid #8B99B1;
}

span:is(.dashed *) {
  border-top: 2px dashed #8B99B1;
}
</style>
